import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { memo } from "react";
import { Field } from "react-final-form";
import { countrySelectData } from "../../../../data/countriesData";
import { useVerificationContext } from "../../context/useVerification";
import {
  isNoTaxIdReasonCode,
  parseTaxEntries,
  TaxEntry,
} from "../../utils/parseTaxEntries";

const MAX_TAX_IDS = 3;

type Props = {
  name: string;
  label: string;
  multiple?: boolean;
};

function getInitialEntries(value: unknown): TaxEntry[] {
  return parseTaxEntries(value, "");
}

function isEqualTaxEntries(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function getCountryLabel(country: string): string {
  return (
    countrySelectData.find((item) => item.value === country)?.label ??
    country
  ).toString();
}

export const TaxIdEditField = memo((props: Props) => {
  const { name, label, multiple } = props;
  const { countriesRegex = {} } = useVerificationContext();

  return (
    <Field
      name={name}
      format={getInitialEntries}
      parse={(entries: TaxEntry[]) => entries}
      isEqual={isEqualTaxEntries}
      render={({ input }) => {
        const entries = Array.isArray(input.value)
          ? (input.value as TaxEntry[])
          : getInitialEntries(input.value);
        const displayEntries = multiple ? entries : entries.slice(0, 1);

        const updateEntry = (
          index: number,
          field: "country" | "value",
          newValue: string
        ) => {
          const next = multiple ? [...entries] : displayEntries.slice(0, 1);
          if (!next[index]) {
            next[index] = { country: "", value: "" };
          }
          next[index] = {
            country:
              field === "country" ? newValue : next[index]?.country || "",
            value: field === "value" ? newValue : next[index]?.value || "",
          };
          input.onChange(next);
          input.onBlur();
        };

        const addEntry = () => {
          if (!multiple || entries.length >= MAX_TAX_IDS) return;
          input.onChange([...entries, { country: "", value: "" }]);
          input.onBlur();
        };

        const removeEntry = (index: number) => {
          const next = entries.filter((_, entryIndex) => entryIndex !== index);
          input.onChange(next.length > 0 ? next : [{ country: "", value: "" }]);
          input.onBlur();
        };

        const canAddMore = multiple && entries.length < MAX_TAX_IDS;

        return (
          <Stack spacing={1.5}>
            {displayEntries.map((entry, index) => {
              const regexConfig = countriesRegex?.[entry.country];
              const regex = regexConfig?.regexp
                ? new RegExp(regexConfig.regexp)
                : null;
              const check =
                regex && entry.value && !isNoTaxIdReasonCode(entry.value)
                  ? regex.test(entry.value)
                  : "none";
              const countryLabelId = `${name}-tax-country-label-${index}`;

              return (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
                >
                  <FormControl variant="filled" sx={{ flexBasis: "35%" }}>
                    <InputLabel id={countryLabelId}>Country</InputLabel>
                    <Select
                      variant="filled"
                      labelId={countryLabelId}
                      label="Country"
                      name={`${input.name}.${index}.country`}
                      value={entry.country || ""}
                      onBlur={input.onBlur}
                      onFocus={input.onFocus}
                      onChange={(event: SelectChangeEvent<string>) =>
                        updateEntry(index, "country", event.target.value)
                      }
                      renderValue={(value) =>
                        value ? getCountryLabel(value) : "None"
                      }
                      inputProps={{
                        "data-testid": `${name}-tax-country-${index}`,
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {countrySelectData.map((item) => (
                        <MenuItem
                          key={String(item.value)}
                          value={String(item.value)}
                        >
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    variant="filled"
                    label={label + (check ? "" : " (check the format)")}
                    value={entry.value || ""}
                    helperText={regexConfig?.example || ""}
                    onBlur={input.onBlur}
                    onFocus={input.onFocus}
                    onChange={(event) =>
                      updateEntry(index, "value", event.target.value)
                    }
                    sx={{
                      flexBasis: "65%",
                      "& label": {
                        color:
                          check === "none"
                            ? undefined
                            : check
                              ? "green !important"
                              : "orange !important",
                      },
                    }}
                    inputProps={{
                      "data-testid": `${name}-tax-value-${index}`,
                    }}
                  />
                  {multiple && index > 0 && (
                    <IconButton
                      aria-label="Delete tax ID"
                      data-testid={`${name}-tax-delete-${index}`}
                      onClick={() => removeEntry(index)}
                      sx={{ mt: 1 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              );
            })}
            {canAddMore && (
              <Button
                variant="text"
                startIcon={<AddIcon />}
                onClick={addEntry}
                data-testid={`${name}-tax-add`}
                sx={{ alignSelf: "flex-start", textTransform: "none" }}
              >
                + Add Another Tax ID
              </Button>
            )}
          </Stack>
        );
      }}
    />
  );
});
